import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/theme";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, AButton, Badge, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";
import { toast } from "sonner";

export default function AdminProducts() {
  const { c } = useAdminTheme();
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = () => api("/admin/products").then(setProducts).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);
  if (!products) return <Spinner full />;

  const shown = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  const del = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api(`/products/${id}`, { method: "DELETE" }); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <div className="fade-up">
      <AdminPageTitle title="Products" subtitle={`${products.length} items`}
        action={<AButton data-testid="new-product" onClick={() => navigate("/admin/product/new")}><Plus size={15} style={{ verticalAlign: "middle", marginRight: 6 }} />New Product</AButton>} />

      <div style={{ position: "relative", maxWidth: 360, marginBottom: 16 }}>
        <Search size={16} color={c.mutedText} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
          style={{ width: "100%", padding: "10px 12px 10px 36px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.surface, color: c.onSurface, outline: "none", fontFamily: "DM Sans" }} />
      </div>

      <ACard style={{ padding: 0, overflow: "hidden" }}>
        {shown.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} onClick={() => navigate(`/admin/product/${p.id}`)}
            style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderBottom: `1px solid ${c.divider}`, cursor: "pointer" }}>
            <div style={{ width: 48, height: 48, borderRadius: 8, overflow: "hidden", background: c.surfaceAlt, flexShrink: 0 }}>
              {p.images?.[0] && <img src={p.images[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }} className="clamp-1">{p.name}</div>
              <div style={{ fontSize: 12.5, color: c.mutedText, textTransform: "capitalize" }}>{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</div>
            </div>
            {p.featured && <Badge tone="primary">Featured</Badge>}
            <Badge tone={p.stock > 5 ? "success" : p.stock > 0 ? "warning" : "danger"}>{p.stock} stock</Badge>
            <div style={{ fontWeight: 700, minWidth: 70, textAlign: "right" }}>{inr(p.price)}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <AButton variant="ghost" style={{ padding: 8 }} onClick={(e) => { e.stopPropagation(); navigate(`/admin/product/${p.id}/edit`); }}><Pencil size={15} /></AButton>
              <AButton variant="ghost" style={{ padding: 8, color: c.danger }} data-testid={`delete-product-${p.id}`} onClick={(e) => del(e, p.id, p.name)}><Trash2 size={15} /></AButton>
            </div>
          </div>
        ))}
      </ACard>
    </div>
  );
}
